/**
 * The spellbook's Weaves tab (docs/spells-depth.md 5): the discovery book. Every
 * woven piece by tier; the ones this browser has seen made show their recipe,
 * what they mean and what they do in each cast mode; the rest are "???",
 * with one parent shown once you know it. Then the Grand Magicks. Rebuilt
 * each time it opens, since discoveries change during play.
 */

import type { Element, GrandId } from '@shared/index.js';
import {
  CAST_MODES, GRAND_IDS, GRAND_MAGICKS, LESSER_STOP, MAGICKS, WOVEN_INFO, WOVEN_LIST, elementName, isWoven, recipeOf, resolveCast, spellName, tierOf,
} from '@shared/index.js';
import { chip, el, keycap } from './dom.js';

export interface BookView {
  knows(el: Element): boolean;
  hasCast(id: GrandId): boolean;
  /** How long the cheat code keeps every page open, in ms (discoveries.ts). */
  revealedFor?(): number;
  /** What you have really found, whatever is showing. */
  readonly pieceCount?: number;
}

const MODE_SHORT = { force: 'Force', area: 'Area', self: 'Self', weapon: 'Weapon' } as const;

/** Base elements and the key fusions are always known; woven pieces once seen. */
function known(book: BookView, e: Element): boolean {
  return !isWoven(e) || book.knows(e);
}

function pieceCard(book: BookView, e: Element): HTMLElement {
  const r = recipeOf(e);
  const tier = tierOf(e);
  if (!book.knows(e)) {
    // A nudge, not the answer: a parent you already know shows.
    const hint = r ? [r.a, r.b].find((p) => known(book, p)) : undefined;
    return el('div', { class: 'combo-card weave-card weave-unknown' },
      el('div', { class: 'combo-head' }, el('span', { class: 'combo-n' }, '???'), el('span', { class: 'combo-mode' }, `Tier ${tier}`)),
      el('div', { class: 'keys' }, hint ? chip(hint) : el('span', { class: 'chip chip-unknown' }, '?'), el('span', { class: 'then' }, '+'), el('span', { class: 'chip chip-unknown' }, '?')),
    );
  }
  const info = isWoven(e) ? WOVEN_INFO[e] : null;
  const across = el('div', { class: 'across' },
    ...CAST_MODES.map((m) => el('span', { class: 'across-item' }, el('span', { class: 'across-mode' }, MODE_SHORT[m]), ' ', spellName(resolveCast([e], m)))));
  return el('div', { class: 'combo-card weave-card' },
    el('div', { class: 'combo-head' }, chip(e), ' ', el('span', { class: 'combo-n' }, elementName(e)), el('span', { class: 'combo-mode' }, `Tier ${tier}`)),
    r ? el('div', { class: 'keys' }, chip(r.a), el('span', { class: 'then' }, '+'), chip(r.b), el('span', { class: 'then' }, 'then'), keycap('C')) : null,
    info ? el('div', { class: 'combo-result' }, el('strong', {}, info.means), ' ', info.force) : null,
    across,
  );
}

function grandCard(book: BookView, id: GrandId): HTMLElement {
  const g = GRAND_MAGICKS[id];
  const ready = g.pieces.every((p) => known(book, p));
  if (!ready) {
    return el('div', { class: 'magick-row weave-unknown' },
      el('div', { class: 'magick-name' }, '???'),
      el('div', { class: 'magick-desc' }, `A Grand Magick of ${g.pieces.length} pieces. Make them all to learn its name.`),
    );
  }
  return el('div', { class: 'magick-row' },
    el('div', { class: 'magick-name' }, g.name, book.hasCast(id) ? '' : el('span', { class: 'muted' }, ' (not yet cast)')),
    el('div', { class: 'keys' }, ...g.pieces.map((p) => chip(p)), el('span', { class: 'then' }, '+'), keycap('Space')),
    el('div', { class: 'magick-desc' }, `${g.desc} Incant ${g.incant} s; cooldown ${g.cooldown} s (${g.pvpOff ? 'not in PvP' : `${g.pvpCooldown} s in PvP`}).`),
  );
}

/** The Lesser Time Stop: an ordinary Magick with a woven piece in it, shown here beside the Grand one once a Moment is known. */
function lesserCard(book: BookView): HTMLElement | null {
  if (!book.knows('moment')) return null;
  const m = MAGICKS.stoptime;
  return el('div', { class: 'magick-row' },
    el('div', { class: 'magick-name' }, m.name, el('span', { class: 'muted' }, ' (a Magick, no incantation)')),
    el('div', { class: 'keys' }, ...m.seq.map((p) => chip(p)), el('span', { class: 'then' }, '+'), keycap('Space')),
    el('div', { class: 'magick-desc' }, `${m.desc} PvP: ${LESSER_STOP.pvpSeconds} s, the other team at ${LESSER_STOP.pvpScale * 100}%.`),
  );
}

/** "1:42": what's left of the reveal. */
function clockOf(ms: number): string {
  const secs = Math.ceil(ms / 1000);
  return `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
}

export function buildWeaves(book: BookView): HTMLElement {
  const found = book.pieceCount ?? WOVEN_LIST.filter((e) => book.knows(e)).length;
  const grands = GRAND_IDS.filter((id) => book.hasCast(id)).length;
  const revealed = book.revealedFor?.() ?? 0;
  const wrap = el('div', { class: 'tab-body' },
    el('p', { class: 'muted intro' },
      keycap('C'), ' weaves the last two pieces in your queue into something deeper; deeper weaves take longer, and a big hit breaks them. ',
      keycap('Z'), ' and ', keycap('X'), ' put a woven piece in your stash, and pull it back whole: that is how Lava meets Ice. ',
      'A Grand Magick casts with ', keycap('Space'), ' when your queue and stash hold its pieces.'),
    el('p', { class: 'weave-progress' }, `Discovered ${found} / ${WOVEN_LIST.length} · Grand Magicks cast ${grands} / ${GRAND_IDS.length}`),
    revealed > 0 ? el('p', { class: 'weave-progress weave-revealed' }, `Everything revealed for another ${clockOf(revealed)}. None of it is saved.`) : null,
  );
  for (const tier of [2, 3, 4, 5] as const) {
    const pieces = WOVEN_LIST.filter((e) => tierOf(e) === tier);
    if (pieces.length === 0) continue;
    wrap.appendChild(el('h3', { class: 'group-title' }, tier === 2 ? 'Tier 2: woven pairs' : tier === 5 ? 'Tier 5: concepts' : `Tier ${tier}`));
    wrap.appendChild(el('div', { class: 'combo-grid' }, ...pieces.map((e) => pieceCard(book, e))));
  }
  wrap.appendChild(el('h3', { class: 'group-title' }, 'Grand Magicks'));
  wrap.appendChild(el('div', { class: 'magick-list' }, ...GRAND_IDS.map((id) => grandCard(book, id)), lesserCard(book)));
  return wrap;
}
